import { CheckSquareTwoTone, CloseSquareTwoTone, LoadingOutlined } from "@ant-design/icons";
import { Badge, notification, Popover, Spin, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import IntlMessage from "components/util-components/IntlMessage";
import React, { useEffect, useState } from "react";
import EcosystemService from "services/EcosystemService";

type Props = {};
type ApisData = { [key: string]: boolean };

const DownMonitorBadge = (props: Props) => {
  const [apisData, setApisData] = useState<ApisData | undefined>();
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchApiIntegrity = async () => {
      // Não inicia a busca se o componente já foi desmontado
      if (!isMounted) return;

      try {
        setFetching(true);
        const response = await EcosystemService.getApisIntegrity();
        if (isMounted) {
          setApisData(response);
        }
      } catch (error: any) {
        if (!isMounted) return; // Não mostra notificação se desmontado
        let errorMessageId = "downMonitor.fetchError";
        let description = null;

        if (error.message.includes("Network Error") || error.name === "TypeError") {
          errorMessageId = "downMonitor.networkError"; // "Problema de rede ou CORS bloqueado"
          description = "Possível falha na comunicação com o servidor.";
        } else if (error.message.includes("timeout")) {
          errorMessageId = "downMonitor.timeoutError"; // "Tempo de resposta excedido"
          description = "Tente novamente mais tarde.";
        } else if (error.response && error.response.status) {
          errorMessageId = "downMonitor.serverError";
          description = `Código: ${error.response.status}. Contate o suporte se persistir.`;
        }

        notification.error({
          message: <IntlMessage id={errorMessageId} />,
          description,
        });

        console.error("Erro ao fetch APIs:", error);
      } finally {
        if (isMounted) {
          setFetching(false);
        }
      }
    };

    fetchApiIntegrity();
    const interval = setInterval(
      () => fetchApiIntegrity(),
      Math.random() * 300000 + 180000
    );
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const getBadgeColor = (apisData: ApisData | undefined) => {
    if (!apisData) return undefined;

    const allAreTrue = Object.values(apisData).reduce(
      (v1, v2) => v1 && v2,
      true
    );
    if (allAreTrue) return "green";

    const allAreFalse = Object.values(apisData).reduce(
      (v1, v2) => v1 || v2,
      false
    );
    if (allAreFalse) return "red";

    return "yellow";
  };

  const ApiPopover = () => {
    if (!apisData) return <IntlMessage id="loadingLabel" />;

    const columns: ColumnsType<any> = [
      {
        key: "uri",
        dataIndex: "uri",
      },
      {
        key: "status",
        dataIndex: "status",
        align: "center",
        render: (status) =>
          status ? (
            <CheckSquareTwoTone twoToneColor="green" />
          ) : (
            <CloseSquareTwoTone twoToneColor="red" />
          ),
      },
    ];

    const apis = Object.keys(apisData);
    const dataSource = apis.map((uri) => ({
      uri,
      status: apisData[uri],
    }));

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        showHeader={false}
      />
    );
  };


  return (
    <Popover
      content={<ApiPopover />}
      title={<IntlMessage id="downMonitor.title" />}
    >
      <Spin
        indicator={<LoadingOutlined spin />}
        spinning={!apisData && fetching}
      >
        {
          apisData &&
          <Badge
            dot
            showZero
            size="default"
            status={fetching ? "processing" : "success"}
            color={getBadgeColor(apisData)}
          />
        }
      </Spin>
    </Popover>
  );
};

export default DownMonitorBadge;
